import { useState, useEffect, useRef } from 'react';

export default function EducationSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const educationData = [
    {
      title: '건국대학교 컴퓨터공학부',
      period: '2018 ~ 2024',
      type: '학사 졸업',
      icon: 'ri-graduation-cap-line',
      details: ['컴퓨터공학 전공', '학사 학위 취득']
    }
  ];

  const awards = [
    {
      title: '학생회장 활동',
      description: '40명 이상 조직 리더십 경험',
      icon: 'ri-team-line'
    },
    {
      title: '학사 대표 수상',
      description: '학업 성취도 인정',
      icon: 'ri-award-line'
    },
    {
      title: '광진구청장상',
      description: '지역사회 기여 인정',
      icon: 'ri-medal-line'
    }
  ];

  const achievements = [
    {
      title: '내일배움캠프 알고리즘 챌린지',
      description: '154/218 문제 해결',
      progress: 70,
      icon: 'ri-code-line'
    },
    {
      title: '최종 프로젝트 챌린지팀',
      description: '130명 중 top-tier 선발',
      progress: 95,
      icon: 'ri-trophy-line'
    }
  ];

  const certificates = [
    {
      title: 'SQLD',
      issuer: '한국데이터산업진흥원',
      year: '2024',
      icon: 'ri-database-2-line'
    },
    {
      title: '정보처리기사',
      issuer: '한국산업인력공단',
      year: '2025',
      icon: 'ri-computer-line'
    }
  ];

  return (
    <section ref={sectionRef} className="py-20 px-6 bg-[#0d1117]">
      <div className="max-w-6xl mx-auto">
        <div className={`transition-all duration-1000 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
            <span className="bg-gradient-to-r from-[#3ECF8E] to-[#2dd4bf] bg-clip-text text-transparent">
              Education & Awards
            </span>
          </h2>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column - Education & Certificates */}
            <div className="space-y-8">
              {/* Education */}
              <div className="bg-[#161b22] border border-[#30363d] rounded-xl p-8 hover:border-[#3ECF8E]/50 transition-all duration-300">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <i className="ri-school-line text-[#3ECF8E]" />
                  학력
                </h3>
                {educationData.map((edu, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#3ECF8E] to-[#2dd4bf] rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${edu.icon} text-xl text-black`} />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-white mb-2">{edu.title}</h4>
                      <div className="flex items-center gap-4 mb-3">
                        <span className="px-3 py-1 bg-[#0d1117] border border-[#30363d] rounded-lg text-sm text-gray-300 font-mono">
                          {edu.period}
                        </span>
                        <span className="px-3 py-1 bg-gradient-to-r from-[#3ECF8E] to-[#2dd4bf] text-black text-sm font-medium rounded-full">
                          {edu.type}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {edu.details.map((detail, detailIndex) => (
                          <div key={detailIndex} className="flex items-center gap-2 text-gray-300">
                            <i className="ri-check-line text-[#3ECF8E]" />
                            <span>{detail}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Certificates */}
              <div className="bg-[#161b22] border border-[#30363d] rounded-xl p-8 hover:border-[#3ECF8E]/50 transition-all duration-300">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <i className="ri-file-text-line text-[#3ECF8E]" />
                  자격증
                </h3>
                <div className="space-y-4">
                  {certificates.map((cert, index) => (
                    <div key={index} className="flex items-center gap-4 p-4 bg-[#0d1117] border border-[#30363d] rounded-lg hover:border-[#3ECF8E]/30 transition-all duration-300">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#3ECF8E] to-[#2dd4bf] rounded-lg flex items-center justify-center">
                        <i className={`${cert.icon} text-lg text-black`} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-white">{cert.title}</h4>
                        <p className="text-sm text-gray-400">{cert.issuer} · {cert.year}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Awards & Achievements */}
            <div className="space-y-8">
              {/* Awards */}
              <div className="bg-[#161b22] border border-[#30363d] rounded-xl p-8 hover:border-[#3ECF8E]/50 transition-all duration-300">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <i className="ri-award-line text-[#3ECF8E]" />
                  수상 및 활동
                </h3>
                <div className="space-y-4">
                  {awards.map((award, index) => (
                    <div
                      key={index}
                      className={`flex items-start gap-4 p-4 bg-[#0d1117] border border-[#30363d] rounded-lg hover:border-[#3ECF8E]/30 transition-all duration-500 ${
                        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
                      }`}
                      style={{ transitionDelay: `${index * 200}ms` }}
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-[#3ECF8E] to-[#2dd4bf] rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className={`${award.icon} text-lg text-black`} />
                      </div>
                      <div>
                        <h4 className="font-bold text-white mb-1">{award.title}</h4>
                        <p className="text-sm text-gray-400">{award.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Achievements */}
              <div className="bg-[#161b22] border border-[#30363d] rounded-xl p-8 hover:border-[#3ECF8E]/50 transition-all duration-300">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <i className="ri-trophy-line text-[#3ECF8E]" />
                  주요 성과
                </h3>
                <div className="space-y-6">
                  {achievements.map((achievement, index) => (
                    <div
                      key={index}
                      className={`transition-all duration-700 ${
                        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                      }`}
                      style={{ transitionDelay: `${index * 300}ms` }}
                    >
                      <div className="flex items-start gap-4 mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#3ECF8E] to-[#2dd4bf] rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className={`${achievement.icon} text-lg text-black`} />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-white mb-1">{achievement.title}</h4>
                          <p className="text-sm text-gray-400 mb-3">{achievement.description}</p>
                          
                          {/* Progress Bar */}
                          <div className="w-full bg-[#0d1117] rounded-full h-2 border border-[#30363d]">
                            <div
                              className={`h-2 bg-gradient-to-r from-[#3ECF8E] to-[#2dd4bf] rounded-full transition-all duration-1000 ${
                                isVisible ? 'w-full' : 'w-0'
                              }`}
                              style={{ 
                                width: isVisible ? `${achievement.progress}%` : '0%',
                                transitionDelay: `${(index * 300) + 500}ms`
                              }}
                            />
                          </div>
                          <div className="text-right mt-1">
                            <span className="text-xs text-[#3ECF8E] font-mono">{achievement.progress}%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
